import { useState } from 'react';

import Button, { type ButtonVariant } from 'components/Button';
import { PartialPopover } from 'components/Popover';
import { useGlobalMemberPopoversContext } from 'contexts';
import { useChat } from 'hooks';
import type { ActionLink as ActionLinkModel } from 'models/sitecore';
import type { PartialIdString } from 'types';
import { is } from 'utils/helpers';

function getPhoneNumberFromText(text: string | undefined): string {
	// Replace all non-digits.
	return text?.replaceAll(/\D/g, '') || '';
}
export function isValidPhoneNumberText(text: string | undefined): boolean {
	return getPhoneNumberFromText(text).length >= 5;
}

function openOneTrustSettings() {
	if (is.func(globalThis.OneTrust?.ToggleInfoDisplay)) {
		globalThis.OneTrust.ToggleInfoDisplay();
	}
}

interface Props extends ActionLinkModel {
	buttonVariant?: ButtonVariant;
	className?: string;
	underline?: boolean;
}

export default function ActionLink({
	buttonVariant = 'secondary',
	className,
	href: hrefProp,
	icon,
	target,
	targetTitle,
	text,
	type,
	underline = false,
}: Props) {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const { openChatOrPopover } = useChat();
	const { openLogin } = useGlobalMemberPopoversContext();

	if (!type || !text) {
		return null;
	}

	const href =
		type === 'Phone'
			? `tel:${getPhoneNumberFromText(text) || getPhoneNumberFromText(hrefProp)}`
			: hrefProp;
	const isPopover = type === 'Popover' && href;

	// TypeScript doesn't like href + target when both are potentially undefined,
	// be explicit about the link case.
	if (is.oneOf(type, 'Phone', 'Link') && href) {
		return (
			<Button
				className={className}
				href={href}
				leadingIcon={icon}
				target={target}
				underline={underline}
				variant={buttonVariant}
			>
				{text}
			</Button>
		);
	}

	return (
		<>
			<Button
				className={className}
				leadingIcon={icon}
				underline={underline}
				variant={buttonVariant}
				aria-haspopup={isPopover ? 'dialog' : undefined}
				onClick={() => {
					if (isPopover) {
						setIsPopoverOpen(true);
					}
					if (type === 'Cookies') {
						openOneTrustSettings();
					}
					if (type === 'Chat') {
						openChatOrPopover();
					}
					if (is.oneOf(type, 'LoginJulaClub', 'LoginJulaPro')) {
						if (type === 'LoginJulaClub') {
							openLogin();
						} else if (type === 'LoginJulaPro') {
							openLogin();
						}
					}
				}}
			>
				{text}
			</Button>
			{isPopover && (
				<PartialPopover
					heading={targetTitle || text}
					isOpen={isPopoverOpen}
					partial={href as PartialIdString}
					onClose={() => {
						setIsPopoverOpen(false);
					}}
				/>
			)}
		</>
	);
}
ActionLink.displayName = 'ActionLink';
